/* eslint-disable no-console */
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc.js';
import timezone from 'dayjs/plugin/timezone.js';
import { ChannelMask } from './channel-mask.js';
import { LogLevel, getStringFromLogLevel } from './log-level.js';
import { Logger } from './logger.js';
import { isNullOrUndefinedOrEmpty } from './null-safety.js';

dayjs.extend(utc);
dayjs.extend(timezone);

export class ConsoleLogger extends Logger {
  private static readonly dateFormat: string = 'YYYY-MM-DD @ HH:mm:ss';
  private static readonly timezone: string = 'Europe/Bucharest';

  constructor(
    public readonly logLevel: LogLevel,
    private readonly appName: string,
    channels: ChannelMask | null = null,
  ) {
    super(channels);
  }

  private getLogPrefix(logLevel: LogLevel): string {
    const appName = this.appName;
    const level = getStringFromLogLevel(logLevel);
    const time = dayjs()
        .tz(ConsoleLogger.timezone)
        .format(ConsoleLogger.dateFormat);

    return `[${level} | ${time} | ${appName}]`;
  }

  logDebug(message: string, args?: { channel?: string }): void {
    if(this.meetsPriority(LogLevel.Debug) && this.isChannelOn(args?.channel)) {
      const prefix = this.getLogPrefix(LogLevel.Debug);
      console.debug(`${prefix} ${message}`);
    }
  }

  logInfo(message: string, args?: { channel?: string }): void {
    if(this.meetsPriority(LogLevel.Info) && this.isChannelOn(args?.channel)) {
      const prefix = this.getLogPrefix(LogLevel.Info);
      console.info(`${prefix} ${message}`);
    }
  }

  logWarning(warning: string, args?: { channel?: string }): void {
    if(this.meetsPriority(LogLevel.Warning) && this.isChannelOn(args?.channel)) {
      const prefix = this.getLogPrefix(LogLevel.Warning);
      console.warn(`${prefix} ${warning}`);
    }
  }

  logError(error: string, args?: { channel?: string, stackTrace?: string }): void {
    if(this.meetsPriority(LogLevel.Error) && this.isChannelOn(args?.channel)) {
      const prefix = this.getLogPrefix(LogLevel.Error);
      if(isNullOrUndefinedOrEmpty(args?.stackTrace)) {
        console.error(`${prefix} ${error}`);
      }
      else {
        console.error(`${prefix} ${error} ${args?.stackTrace}`);
      }
    }
  }

  logException(exception: string, args?: { channel?: string, stackTrace?: string }): void {
    if(this.meetsPriority(LogLevel.Exception) && this.isChannelOn(args?.channel)) {
      const prefix = this.getLogPrefix(LogLevel.Exception);
      if(isNullOrUndefinedOrEmpty(args?.stackTrace)) {
        console.error(`${prefix} ${exception}`);
      }
      else {
        console.error(`${prefix} ${exception} ${args?.stackTrace}`);
      }
    }
  }

  logFatal(fatalException: string, args?: { channel?: string, stackTrace?: string}): void {
    if(this.meetsPriority(LogLevel.Fatal) && this.isChannelOn(args?.channel)) {
      const prefix = this.getLogPrefix(LogLevel.Fatal);
      if(isNullOrUndefinedOrEmpty(args?.stackTrace)) {
        console.error(`${prefix} ${fatalException}`);
      }
      else {
        console.error(`${prefix} ${fatalException} ${args?.stackTrace}`);
      }
    }
  }
}
